// Notifications inbox data layer (#809). `GET /v1/notifications` yields one
// cursor-paged slice of the caller's inbox plus the account-wide `unread_count`.
// The bell accumulates pages through `usePagedQuery` and renders THOSE rows only.
// The account stream's `notifications` topic says WHEN to re-read
// (`lib/notification-push.ts`), never what to show: its rows used to be merged
// over the pages, so the panel listed whatever the wire last said rather than
// what the server answers (§ Boundary: re-read, never patch from the echo).

import type { NotificationStatus, NotificationWire } from '@developerz/domain';
import { NotificationWireSchema } from '@developerz/domain';
import { createQuery } from '@tanstack/solid-query';
import { createMemo, createSignal } from 'solid-js';
import { z } from 'zod';
import { activeAccountId } from './active-account';
import { apiFetch, withCursor } from './api';
import { type Page, usePagedQuery } from './paged';
import { pageSchema } from './paged-schema';
import { SESSION_TOKEN } from './session';

/**
 * The ledger's delivery status, IMPORTED from the domain rather than restated:
 * a hand-copied union stops covering the vocabulary the moment a status is
 * added, silently. It was `'pending' | 'sent' | 'failed' | 'suppressed'` here
 * while the ledger gained `no_target` — the type erases at build (`import
 * type`), so nothing of the domain reaches the SPA bundle.
 */
export type { NotificationStatus };

/**
 * One inbox row — `NotificationWireSchema` from `@developerz/domain`, THE
 * PUBLISHED RESOURCE (`NotificationResource`), which `GET /v1/notifications` and
 * the account stream's `notifications` topic both emit.
 *
 * It replaces a mirror that disagreed with the projection on FIVE fields, all
 * invisible because `apiFetch` ends in a cast: `kind` and `channel` were widened
 * to bare `string` (they are `NotificationKindSchema` / `NotificationChannelSchema`
 * — the wire package's own header names this mirror as the reason it says so),
 * and `read_at`, `payload` and `dedupe_key` were declared OPTIONAL against a
 * projection that emits all three unconditionally. `read_at` null still means
 * unread; read is an EVENT property, stamped per channel row.
 */
export type NotificationRow = Readonly<NotificationWire>;

/** Inbox page: the canonical slice plus the account-wide unread EVENT count. */
export type NotificationsPage = Page<NotificationRow> & { readonly unread_count: number };

/**
 * The inbox body, PARSED (plan 101 slice 15 step 3). It found two vocabulary
 * defects on its first run — the mock plane served `channel: 'web_push'` and the
 * bell's test fixture `kind: 'digest'`, neither of which any producer in this
 * repo emits — so both fixtures had been describing rows the platform cannot
 * make. Invisible while the door ended in a cast.
 */
const notificationsPageSchema = pageSchema(NotificationWireSchema).extend({
  unread_count: z.number(),
});

/** Both read POSTs answer with the fresh account-wide count. */
type MarkReadResponse = { readonly ok: boolean; readonly unread_count: number };

export const NOTIFICATIONS_QUERY_KEY = 'notifications';

/** One page of the caller's notifications. Idle with no session, like `useMe`. */
export function useNotifications(
  cursor: () => string | null,
  token: string | null = SESSION_TOKEN,
) {
  return createQuery<NotificationsPage>(() => ({
    queryKey: [NOTIFICATIONS_QUERY_KEY, cursor(), token, activeAccountId()],
    enabled: token !== null,
    queryFn: () => {
      const at = cursor();
      const path = withCursor('/v1/notifications', at);
      return apiFetch(path, token, undefined, { schema: notificationsPageSchema });
    },
  }));
}

/**
 * The bell's data layer: the paged inbox plus the unread badge and its two
 * writes. The badge is the API's account-wide `unread_count` — never the
 * loaded-row count — with a timestamped local layer so a mark-read
 * decrements/zeroes it optimistically; latest write wins, so a fresher server
 * read (refetch, next page) supersedes a stale optimistic value and a settled
 * POST supersedes its own guess with the authoritative count — unless a
 * later-SENT POST already answered (`settle`).
 */
export function useNotificationInbox(token: string | null = SESSION_TOKEN) {
  let query!: ReturnType<typeof useNotifications>;
  const paged = usePagedQuery<NotificationRow>((cursor) => {
    query = useNotifications(cursor, token);
    return query;
  });
  const [local, setLocal] = createSignal<{ count: number; at: number } | null>(null);
  // Sticky across page fetches: `data` is undefined mid re-key, and every page's
  // response carries a fresh account-wide count — not just page one's.
  const server = createMemo<{ count: number; at: number }>(
    (prev) =>
      query.data === undefined ? prev : { count: query.data.unread_count, at: query.dataUpdatedAt },
    { count: 0, at: 0 },
  );
  const unread = (): number => {
    const l = local();
    const s = server();
    return l !== null && l.at >= s.at ? l.count : s.count;
  };
  // Mark-read POSTs are ordered by when they were SENT, never by when their
  // answers arrive: the server applies them in send order, so an earlier POST's
  // answer landing last carries an OLDER count (unread 5; read x → 4, read y → 3;
  // y answers first — x's 4 must not overwrite it). `sent` numbers each write at
  // call time; `applied` is the newest one whose answer is on the badge.
  let sent = 0;
  let applied = 0;
  // Adopt the POST's authoritative count unless a later-sent one already
  // answered; on failure of the NEWEST write drop the optimistic layer so the
  // badge falls back to the last server read instead of lying (an older write's
  // failure leaves a newer write's guess standing).
  const settle = (post: Promise<MarkReadResponse>): void => {
    sent += 1;
    const seq = sent;
    post
      .then((r) => {
        if (seq <= applied) return;
        applied = seq;
        setLocal({ count: r.unread_count, at: Date.now() });
      })
      .catch(() => {
        if (seq === sent && seq > applied) setLocal(null);
      });
  };
  // Sticky: the first read has LANDED, empty or not (`notification-push.ts`
  // needs "landed empty" told apart from "still in flight").
  const settled = createMemo((was: boolean) => was || query.data !== undefined, false);
  return {
    paged,
    unread,
    settled,
    /**
     * Re-read the inbox — rows AND `unread_count` — from page one. Called on
     * push, never on a timer (`notification-push.ts`). `reload()`, not a
     * refetch: a refetch at the cursor in hand merges nothing (`paged.ts`), and
     * a new notification lands at the HEAD. On page one it reconciles in place;
     * past it the accumulation drops back to page one, since the slice under a
     * held cursor has shifted.
     */
    refresh: (): void => paged.reload(),
    /** Mark one row read. No-op for an already-read row. */
    markRead: (n: NotificationRow): void => {
      if (n.read_at != null) return;
      setLocal({ count: Math.max(0, unread() - 1), at: Date.now() });
      settle(
        apiFetch<MarkReadResponse>(`/v1/notifications/${encodeURIComponent(n.id)}/read`, token, {
          method: 'POST',
        }),
      );
    },
    /** Mark every unread row read — clears the badge. */
    markAllRead: (): void => {
      setLocal({ count: 0, at: Date.now() });
      settle(apiFetch<MarkReadResponse>('/v1/notifications/read-all', token, { method: 'POST' }));
    },
  };
}
